import React, { useState } from 'react';

const NAV_ITEMS = [
  { label: 'Home', view: 'home' },
  { label: 'Getting started', view: 'getting-started' },
  { label: 'Family law topics', view: 'topics' },
  { label: 'Forms', view: 'forms' },
  { label: 'Appeals', view: 'appeals' },
  { label: 'Court guide', view: 'court-guide' },
  { label: 'Resources', view: 'resources' },
  { label: 'Directory', view: 'directory' }
];

const EXAMPLE_STEPS = [
  { label: 'Application filed', state: 'done' },
  { label: 'Answer served', state: 'done' },
  { label: 'Case conference', state: 'now' },
  { label: 'Settlement conference', state: 'future' },
  { label: 'Motion', state: 'future' },
  { label: 'Trial preparation', state: 'future' },
  { label: 'Appeal', state: 'future' }
];

const STATS = [
  { n: '40–57', unit: '%', label: 'of family law parties are self-represented' },
  { n: '278,000', unit: '+', label: 'active family law cases in Canada at any time' },
  { n: '$15–50', unit: 'K', label: 'typical cost of a contested family matter' },
  { n: '$0', unit: '', label: 'for referred parents through the protected tier', free: true }
];

function CasePath({ steps = EXAMPLE_STEPS, isExample = true }) {
  const currentIndex = steps.findIndex((step) => step.state === 'now');

  return (
    <div className="gpj-case-path">
      <div className="gpj-case-path__head">
        <h2>Your case path</h2>
        <span className="gpj-case-path__stage">
          Stage {currentIndex + 1} of {steps.length}
        </span>
      </div>

      <ul className="gpj-case-steps">
        {steps.map((step) => (
          <li
            key={step.label}
            className={`gpj-case-step gpj-case-step--${step.state}`}
          >
            {step.label}
          </li>
        ))}
      </ul>

      <div className="gpj-case-deadline">
        <span className="gpj-case-deadline__dot" aria-hidden="true" />
        <span className="gpj-case-deadline__txt">
          <b>Next deadline in 6 days</b>
          Serve your case conference brief
        </span>
      </div>

      {isExample && <p className="gpj-case-path__note">Example view</p>}
    </div>
  );
}

export default function SiteHeader({ currentView, setCurrentView, user, onLogout, showHomeContent = true }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const awaitingAccess = ['awaiting_access_selection', 'awaiting_access_fulfillment']
    .includes(user?.account_state);
  const userHomeView = awaitingAccess
    ? 'access-selection'
    : 'dashboard';
  const userHomeLabel = awaitingAccess
    ? 'Access options'
    : 'Dashboard';
  const navItems = user
    ? [
      ...NAV_ITEMS,
      { label: 'Writing review', view: 'writing-review' },
      ...(user.role === 'admin'
        ? [{ label: 'Referral reviews', view: 'referral-admin' }]
        : [])
    ]
    : NAV_ITEMS;

  const navigate = (view) => {
    setIsMenuOpen(false);
    setCurrentView(view);
  };

  const navigation = (mobile = false) => (
    <>
      {navItems.map((item) => (
        <button
          key={item.view}
          type="button"
          className={`gpj-nav__link${currentView === item.view ? ' is-active' : ''}`}
          onClick={() => navigate(item.view)}
          aria-current={currentView === item.view ? 'page' : undefined}
        >
          {item.label}
        </button>
      ))}
      {mobile && (
        <div className="gpj-nav__mobile-actions">
          {user ? (
            <>
              <button type="button" className="gpj-login" onClick={() => navigate(userHomeView)}>
                {userHomeLabel}
              </button>
              <button type="button" className="gpj-btn gpj-btn--sm gpj-btn--primary" onClick={onLogout}>
                Log out
              </button>
            </>
          ) : (
            <>
              <button type="button" className="gpj-login" onClick={() => navigate('login')}>
                Log in
              </button>
              <button type="button" className="gpj-btn gpj-btn--sm gpj-btn--primary" onClick={() => navigate('register')}>
                Register
              </button>
            </>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      <nav className="gpj-nav" aria-label="Primary navigation">
        <div className="gpj-wrap gpj-nav__inner">
          <button type="button" className="gpj-brand" onClick={() => navigate('home')}>
            <span className="gpj-mark" aria-hidden="true">GPJ</span>
            <span className="gpj-brand__name">Good Parents Justice</span>
          </button>

          <div className="gpj-nav__links">{navigation()}</div>

          <div className="gpj-nav__actions">
            {user ? (
              <>
                <button type="button" className="gpj-login" onClick={() => navigate(userHomeView)}>
                  {userHomeLabel}
                </button>
                <button type="button" className="gpj-btn gpj-btn--sm gpj-btn--primary" onClick={onLogout}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <button type="button" className="gpj-login" onClick={() => navigate('login')}>
                  Log in
                </button>
                <button type="button" className="gpj-btn gpj-btn--sm gpj-btn--primary" onClick={() => navigate('register')}>
                  Register
                </button>
              </>
            )}
          </div>

          <button
            type="button"
            className="gpj-nav__toggle"
            aria-expanded={isMenuOpen}
            aria-controls="gpj-mobile-navigation"
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            Menu
          </button>
        </div>

        {isMenuOpen && (
          <div id="gpj-mobile-navigation" className="gpj-nav__mobile">
            {navigation(true)}
          </div>
        )}
      </nav>

      {showHomeContent && (
        <>
          <header className="gpj-hero">
        <div className="gpj-wrap gpj-hero__grid">
          <div>
            <span className="gpj-eyebrow">Ontario family court</span>
            <h1>
              Justice shouldn't depend on <em>who can afford a lawyer.</em>
            </h1>
            <p className="gpj-hero__lede">
              A guided path through every stage of your case, from your first form to an
              appeal. Built by a parent who went through it without representation.
            </p>
            <div className="gpj-hero__cta">
              <button
                type="button"
                className="gpj-btn gpj-btn--lg gpj-btn--primary"
                onClick={() => navigate(user ? 'dashboard' : 'register')}
              >
                Start your case path
              </button>
              <button
                type="button"
                className="gpj-btn gpj-btn--lg gpj-btn--onnavy"
                onClick={() => navigate(user ? 'access-selection' : 'register')}
              >
                Referral / free access
              </button>
            </div>
            <p className="gpj-hero__disclaim">
              Good Parents Justice provides administrative and procedural support. It is not
              a law firm and does not give legal advice.
            </p>
          </div>

          <CasePath />
        </div>
          </header>

          <section className="gpj-stats-band" aria-label="Family law statistics">
            <div className="gpj-wrap">
              <div className="gpj-stats">
                {STATS.map((stat) => (
                  <div key={stat.label} className={`gpj-stat${stat.free ? ' gpj-stat--free' : ''}`}>
                    <span className="gpj-stat__n">
                      {stat.n}
                      {stat.unit && <span className="unit">{stat.unit}</span>}
                    </span>
                    <span className="gpj-stat__l">{stat.label}</span>
                  </div>
                ))}
              </div>
              <p className="gpj-src">
                Sources: Department of Justice Canada (self-represented litigant prevalence);
                Statistics Canada (active family law cases).
              </p>
            </div>
          </section>
        </>
      )}
    </>
  );
}
